import { getCognitoIdentityProvider } from './sso-providers';
import type {
  AppAuthSession,
  AppAuthUser,
  AuthAdapter,
  AuthChangeEvent,
  AuthMetadata,
  AuthSubscription,
  OAuthProvider,
  SessionResult,
  SignInWithPasswordResult,
  SignUpResult,
  UserResult,
} from './types';

type CognitoAuthResult = {
  AccessToken?: string;
  IdToken?: string;
  RefreshToken?: string;
  ExpiresIn?: number;
};

type CognitoInitiateAuthResponse = {
  AuthenticationResult?: CognitoAuthResult;
  ChallengeName?: string;
  Session?: string;
  ChallengeParameters?: Record<string, string>;
};

type CognitoApiSessionResponse = {
  access_token?: string;
  id_token?: string;
  expires_in?: number;
};

type CognitoAttribute = {
  Name: string;
  Value: string;
};

type PendingNewPasswordChallenge = {
  email: string;
  session: string;
  user: AppAuthUser;
};

type StoredOAuthState = {
  state: string;
  codeVerifier: string;
  redirectTo: string;
};

// Cross-tab notifications. Each tab keeps its session in memory, but every tab
// shares one refresh cookie, so a sign-in or sign-out in one tab changes what
// the others would restore.
type AuthBroadcastMessage = {
  type: 'signed_in' | 'signed_out';
  sub: string;
};

const OAUTH_STORAGE_KEY = 'piloteer:cognito-oauth';
const AUTH_BROADCAST_CHANNEL_NAME = 'piloteer:cognito-auth';
const COGNITO_USER_ATTRIBUTE_NAMES = new Set([
  'email',
  'name',
  'given_name',
  'family_name',
  'phone_number',
]);

const authRegion = import.meta.env.VITE_COGNITO_REGION || 'us-east-1';
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID || '';
const appClientId =
  import.meta.env.VITE_COGNITO_APP_CLIENT_ID_WEB ||
  import.meta.env.VITE_COGNITO_APP_CLIENT_ID ||
  '';
const hostedUiBaseUrl = (
  import.meta.env.VITE_COGNITO_HOSTED_UI_BASE_URL || ''
).replace(/\/$/, '');
const cognitoEndpoint = `https://cognito-idp.${authRegion}.amazonaws.com/`;
const apiBaseUrl = (import.meta.env.VITE_PILOTEER_API_BASE_URL || '').replace(
  /\/$/,
  ''
);
const apiSessionUrl = `${apiBaseUrl}/auth/session`;
const secureLogoutFailure =
  'Secure logout failed: server session revocation was not confirmed. You are still signed in; please try again.';
const accountSwitchedFailure =
  'A different account signed in from another tab. Reload to continue.';

const listeners = new Set<
  (event: AuthChangeEvent, session: AppAuthSession | null) => void
>();
let activeSession: AppAuthSession | null = null;
// Bumped on every session write so an in-flight restore can tell whether a
// login or sign-out overtook it and its result must be discarded.
let sessionGeneration = 0;
let pendingNewPasswordChallenge: PendingNewPasswordChallenge | null = null;
let oauthCallbackPromise: Promise<SessionResult> | null = null;
let refreshSessionPromise: Promise<SessionResult> | null = null;
let restoreSessionPromise: Promise<PersistedSessionResult> | null = null;
// Cognito `sub` of the account this tab last held a session for. Kept after the
// session is cleared so the shared cookie is only cleared while it still
// belongs to this tab's account, and so the tab never adopts a different
// account from the cookie without a page load or its own sign-in.
let tabAccountSub: string | null = null;
// Set once this tab learns that another account took over the shared cookie.
// Until this tab reloads (or signs in itself) it must not adopt that session:
// its UI and cached data belong to the previous account.
let accountSwitched = false;
// Set once the API answers a restore with 401, i.e. there is no cookie session.
// Signed-out pages look the session up from many places; without this each
// lookup would call the refresh endpoint again. Another tab signing in reaches
// this tab as ACCOUNT_SWITCHED and a reload, so only this tab's own sign-in
// needs to clear it.
let cookieSessionAbsent = false;

const emitAuthChange = (
  event: AuthChangeEvent,
  session: AppAuthSession | null
) => {
  listeners.forEach((listener) => listener(event, session));
};

const isConfigured = () => Boolean(authRegion && userPoolId && appClientId);

const cognitoRequest = async <T>(
  target: string,
  body: Record<string, unknown>
): Promise<T> => {
  const response = await fetch(cognitoEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const text = await response.text();
  const data = text ? JSON.parse(text) : {};

  if (!response.ok) {
    const error = new Error(
      data.message || data.__type || 'Cognito authentication failed'
    );
    error.name = (data.__type || 'Error').split('#').pop();
    throw error;
  }

  return data as T;
};

const decodeJwtPayload = (token?: string): Record<string, unknown> => {
  if (!token) return {};
  const payload = token.split('.')[1];
  if (!payload) return {};
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    '='
  );
  try {
    return JSON.parse(window.atob(padded));
  } catch {
    return {};
  }
};

const toAppUser = (
  accessToken?: string,
  idToken?: string,
  attributes?: CognitoAttribute[]
): AppAuthUser | null => {
  const accessClaims = decodeJwtPayload(accessToken);
  const idClaims = decodeJwtPayload(idToken);
  const attributeMap = Object.fromEntries(
    (attributes ?? []).map((attribute) => [attribute.Name, attribute.Value])
  );
  const subject =
    idClaims.sub ||
    accessClaims.sub ||
    attributeMap.sub ||
    attributeMap.username;
  if (!subject || typeof subject !== 'string') return null;

  const email = valueToString(idClaims.email ?? attributeMap.email);
  const emailVerified =
    valueToString(idClaims.email_verified ?? attributeMap.email_verified) ===
    'true';
  const name =
    valueToString(idClaims.name ?? attributeMap.name) ||
    (email ? email.split('@')[0] : undefined);
  const userMetadata: AuthMetadata = {
    name,
    full_name: name,
    role: idClaims['custom:role'],
    needs_password_set: false,
  };

  return {
    id: subject,
    email,
    emailConfirmedAt: emailVerified ? new Date().toISOString() : null,
    email_confirmed_at: emailVerified ? new Date().toISOString() : null,
    userMetadata,
    user_metadata: userMetadata,
    appMetadata: { provider: 'cognito' },
    app_metadata: { provider: 'cognito' },
  };
};

const toAppSession = (
  authResult: CognitoAuthResult,
  existingRefreshToken?: string
): AppAuthSession | null => {
  const accessToken = authResult.AccessToken;
  if (!accessToken) return null;
  const user = toAppUser(accessToken, authResult.IdToken);
  if (!user) return null;

  const expiresAt =
    Math.floor(Date.now() / 1000) + (authResult.ExpiresIn ?? 3600);
  const refreshToken = authResult.RefreshToken ?? existingRefreshToken;
  return {
    provider: 'cognito',
    accessToken,
    access_token: accessToken,
    idToken: authResult.IdToken,
    id_token: authResult.IdToken,
    refreshToken,
    refresh_token: refreshToken,
    expiresAt,
    expires_at: expiresAt,
    user,
  };
};

const valueToString = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return undefined;
};

const saveSession = (session: AppAuthSession | null) => {
  activeSession = session;
  sessionGeneration += 1;
  if (session) {
    tabAccountSub = session.user.id;
    cookieSessionAbsent = false;
  }
};

// A sign-in completed in this tab, so the shared cookie is this tab's again.
const saveSignedInSession = (session: AppAuthSession) => {
  accountSwitched = false;
  saveSession(session);
};

const loadSession = (): AppAuthSession | null => {
  return activeSession;
};

const persistRefreshToken = async (session: AppAuthSession) => {
  if (!session.refreshToken || !apiBaseUrl) return;

  // The access token lets the API label the cookie with its verified owner.
  const response = await fetch(apiSessionUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.accessToken}`,
    },
    body: JSON.stringify({ refresh_token: session.refreshToken }),
  });

  if (!response.ok) {
    throw new Error('Unable to persist Cognito session');
  }
};

const clearPersistedSession = async () => {
  if (!apiBaseUrl) return;

  // The API keeps the cookie when it belongs to a different account, e.g. one
  // that signed in from another tab after this tab's account signed out.
  await fetch(`${apiSessionUrl}/logout`, {
    method: 'POST',
    credentials: 'include',
    ...(tabAccountSub
      ? {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ expected_sub: tabAccountSub }),
        }
      : {}),
  });
};

type PersistedSessionResult = {
  session: AppAuthSession | null;
  // The API confirmed there is no usable cookie session (401). Other failures,
  // such as a 5xx, say nothing about the cookie and may be retried.
  cookieAbsent: boolean;
};

const restorePersistedSession = async (): Promise<PersistedSessionResult> => {
  if (!apiBaseUrl) return { session: null, cookieAbsent: false };

  const response = await fetch(`${apiSessionUrl}/refresh`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    return { session: null, cookieAbsent: response.status === 401 };
  }

  const tokenJson = (await response.json()) as CognitoApiSessionResponse;
  return {
    session: toAppSession({
      AccessToken: tokenJson.access_token,
      IdToken: tokenJson.id_token,
      ExpiresIn: tokenJson.expires_in,
    }),
    cookieAbsent: false,
  };
};

type RestoreSessionResult = {
  session: AppAuthSession | null;
  // True when a login or sign-out overtook the restore; `session` is then the
  // current session, not the restored one, and nothing was emitted.
  stale: boolean;
  // True when the cookie belongs to a different account than the one this tab
  // has shown; nothing was restored and ACCOUNT_SWITCHED was emitted.
  accountSwitched?: boolean;
};

const markAccountSwitched = () => {
  if (accountSwitched) return;
  accountSwitched = true;
  pendingNewPasswordChallenge = null;
  saveSession(null);
  emitAuthChange('ACCOUNT_SWITCHED', null);
};

// Restores the cookie-backed session once for all concurrent callers and only
// applies it if the session is untouched since the restore started. Without
// this, a slow restore for a previously signed-in account could land after a
// fresh login and replace the new account with the old one.
//
// The cookie is shared by every tab, so if another tab signed in a different
// account since this tab last held one, the restore returns that account.
// Adopting it would silently turn this tab's page, and any request it still
// sends, into the other account; the tab reloads to follow it instead.
const restoreSession = async (): Promise<RestoreSessionResult> => {
  if (accountSwitched) {
    return { session: null, stale: false, accountSwitched: true };
  }
  if (cookieSessionAbsent) {
    return { session: null, stale: false };
  }
  const generation = sessionGeneration;
  const expectedSub = tabAccountSub;
  if (!restoreSessionPromise) {
    restoreSessionPromise = restorePersistedSession().finally(() => {
      restoreSessionPromise = null;
    });
  }

  try {
    const { session: restored, cookieAbsent } = await restoreSessionPromise;
    if (generation !== sessionGeneration) {
      return { session: loadSession(), stale: true };
    }
    if (cookieAbsent) cookieSessionAbsent = true;
    if (restored && expectedSub && restored.user.id !== expectedSub) {
      markAccountSwitched();
      return { session: null, stale: false, accountSwitched: true };
    }
    if (restored) {
      saveSession(restored);
      emitAuthChange('TOKEN_REFRESHED', restored);
    }
    return { session: restored, stale: false };
  } catch (error) {
    if (generation !== sessionGeneration) {
      return { session: loadSession(), stale: true };
    }
    throw error;
  }
};

const toPendingPasswordUser = (email: string): AppAuthUser => {
  const userMetadata: AuthMetadata = {
    needs_password_set: true,
  };
  return {
    id: email,
    email,
    emailConfirmedAt: new Date().toISOString(),
    email_confirmed_at: new Date().toISOString(),
    userMetadata,
    user_metadata: userMetadata,
    appMetadata: { provider: 'cognito' },
    app_metadata: { provider: 'cognito' },
  };
};

const savePendingNewPasswordChallenge = (
  challenge: PendingNewPasswordChallenge | null
) => {
  pendingNewPasswordChallenge = challenge;
};

const loadPendingNewPasswordChallenge =
  (): PendingNewPasswordChallenge | null => {
    return pendingNewPasswordChallenge;
  };

const randomBase64Url = async (byteLength = 32): Promise<string> => {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return bytesToBase64Url(bytes);
};

const bytesToBase64Url = (bytes: Uint8Array): string => {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window
    .btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const sha256Base64Url = async (value: string): Promise<string> => {
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(value)
  );
  return bytesToBase64Url(new Uint8Array(digest));
};

const authChannel =
  typeof BroadcastChannel === 'undefined'
    ? null
    : new BroadcastChannel(AUTH_BROADCAST_CHANNEL_NAME);

const postAuthBroadcast = (message: AuthBroadcastMessage) => {
  authChannel?.postMessage(message);
};

const announceSignIn = (session: AppAuthSession) => {
  emitAuthChange('SIGNED_IN', session);
  postAuthBroadcast({ type: 'signed_in', sub: session.user.id });
};

const isAuthBroadcastMessage = (
  value: unknown
): value is AuthBroadcastMessage => {
  if (!value || typeof value !== 'object') return false;
  const { type, sub } = value as Record<string, unknown>;
  return (
    (type === 'signed_in' || type === 'signed_out') &&
    typeof sub === 'string' &&
    sub.length > 0
  );
};

// Another tab signed out or signed in. Its server logout already revoked every
// token of the signed-out account, and a new sign-in replaced the shared
// cookie, so this tab must follow rather than keep acting on stale state.
const handleAuthBroadcast = (message: AuthBroadcastMessage) => {
  const currentSub = loadSession()?.user.id;
  if (message.type === 'signed_out') {
    if (currentSub !== message.sub) return;
    savePendingNewPasswordChallenge(null);
    saveSession(null);
    emitAuthChange('REMOTE_SIGNED_OUT', null);
    return;
  }
  if (currentSub !== message.sub) {
    markAccountSwitched();
  }
};

authChannel?.addEventListener('message', (event) => {
  if (isAuthBroadcastMessage(event.data)) handleAuthBroadcast(event.data);
});

export class CognitoAuthAdapter implements AuthAdapter {
  async getSession(): Promise<SessionResult> {
    const session = loadSession();
    if (!session) {
      try {
        const restored = await restoreSession();
        return { data: { session: restored.session }, error: null };
      } catch (error) {
        return { data: { session: null }, error: error as Error };
      }
    }

    if (session.expiresAt && session.expiresAt - 60 <= Date.now() / 1000) {
      return this.refreshSession();
    }

    return { data: { session }, error: null };
  }

  async getUser(): Promise<UserResult> {
    const { data } = await this.getSession();
    if (!data.session?.accessToken) {
      const pendingChallenge = loadPendingNewPasswordChallenge();
      if (pendingChallenge) {
        return { data: { user: pendingChallenge.user }, error: null };
      }
      return { data: { user: null }, error: null };
    }

    try {
      const response = await cognitoRequest<{
        UserAttributes: CognitoAttribute[];
      }>('GetUser', { AccessToken: data.session.accessToken });
      const user = toAppUser(
        data.session.accessToken,
        data.session.idToken,
        response.UserAttributes
      );
      return { data: { user }, error: null };
    } catch (error) {
      return { data: { user: data.session.user }, error: error as Error };
    }
  }

  async getAccessToken(): Promise<string | null> {
    const { data } = await this.getSession();
    return data.session?.accessToken ?? null;
  }

  async signInWithPassword(args: {
    email: string;
    password: string;
  }): Promise<SignInWithPasswordResult> {
    if (!isConfigured()) {
      return this.errorResult('Cognito is not configured');
    }

    try {
      const response = await cognitoRequest<CognitoInitiateAuthResponse>(
        'InitiateAuth',
        {
          AuthFlow: 'USER_PASSWORD_AUTH',
          ClientId: appClientId,
          AuthParameters: {
            USERNAME: args.email,
            PASSWORD: args.password,
          },
        }
      );

      if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        if (!response.Session) {
          throw new Error(
            'Cognito password setup challenge did not return a session'
          );
        }
        const user = toPendingPasswordUser(args.email);
        savePendingNewPasswordChallenge({
          email: args.email,
          session: response.Session,
          user,
        });
        return { data: { session: null, user }, error: null };
      }

      if (!response.AuthenticationResult) {
        throw new Error(
          response.ChallengeName
            ? `Unsupported Cognito challenge: ${response.ChallengeName}`
            : 'Cognito login did not return a session'
        );
      }

      const session = toAppSession(response.AuthenticationResult);
      if (!session) {
        throw new Error('Cognito login did not return a usable session');
      }
      savePendingNewPasswordChallenge(null);
      saveSignedInSession(session);
      await persistRefreshToken(session);
      announceSignIn(session);
      return { data: { session, user: session?.user ?? null }, error: null };
    } catch (error) {
      return { data: { session: null, user: null }, error: error as Error };
    }
  }

  private async completeNewPasswordChallenge(
    password: string
  ): Promise<UserResult> {
    const pendingChallenge = loadPendingNewPasswordChallenge();
    if (!pendingChallenge?.session) {
      return {
        data: { user: null },
        error: new Error('No pending password setup challenge'),
      };
    }

    try {
      const response = await cognitoRequest<{
        AuthenticationResult?: CognitoAuthResult;
      }>('RespondToAuthChallenge', {
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        ClientId: appClientId,
        Session: pendingChallenge.session,
        ChallengeResponses: {
          USERNAME: pendingChallenge.email,
          NEW_PASSWORD: password,
        },
      });
      if (!response.AuthenticationResult) {
        throw new Error('Password setup did not return a session');
      }
      const session = toAppSession(response.AuthenticationResult);
      if (!session) {
        throw new Error('Password setup did not return a usable session');
      }
      savePendingNewPasswordChallenge(null);
      saveSignedInSession(session);
      await persistRefreshToken(session);
      announceSignIn(session);
      return { data: { user: session?.user ?? null }, error: null };
    } catch (error) {
      return { data: { user: pendingChallenge.user }, error: error as Error };
    }
  }

  async signUp(args: {
    email: string;
    password: string;
    options?: {
      data?: AuthMetadata;
      emailRedirectTo?: string;
    };
  }): Promise<SignUpResult> {
    if (!isConfigured()) {
      return this.errorResult('Cognito is not configured');
    }

    try {
      const userAttributes = Object.entries(args.options?.data ?? {})
        .filter(
          ([name, value]) =>
            COGNITO_USER_ATTRIBUTE_NAMES.has(name) && typeof value === 'string'
        )
        .map(([Name, Value]) => ({ Name, Value }));
      await cognitoRequest('SignUp', {
        ClientId: appClientId,
        Username: args.email,
        Password: args.password,
        UserAttributes: [
          { Name: 'email', Value: args.email },
          ...userAttributes,
        ],
      });
      return { data: { session: null, user: null }, error: null };
    } catch (error) {
      return { data: { session: null, user: null }, error: error as Error };
    }
  }

  async signInWithOAuth(args: {
    provider: OAuthProvider;
    options?: {
      redirectTo?: string;
      queryParams?: Record<string, string>;
    };
  }): Promise<{ error: Error | null }> {
    if (!hostedUiBaseUrl || !appClientId) {
      return { error: new Error('Cognito OAuth is not configured') };
    }

    const redirectTo =
      args.options?.redirectTo ||
      `${window.location.origin}${window.location.pathname}`;
    const state = await randomBase64Url();
    const codeVerifier = await randomBase64Url(64);
    const codeChallenge = await sha256Base64Url(codeVerifier);
    const storedState: StoredOAuthState = {
      state,
      codeVerifier,
      redirectTo,
    };
    sessionStorage.setItem(OAUTH_STORAGE_KEY, JSON.stringify(storedState));

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: appClientId,
      redirect_uri: redirectTo,
      scope: 'openid email profile aws.cognito.signin.user.admin',
      identity_provider: getCognitoIdentityProvider(args.provider),
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      ...(args.options?.queryParams ?? {}),
    });
    window.location.assign(`${hostedUiBaseUrl}/oauth2/authorize?${params}`);
    return { error: null };
  }

  async handleOAuthCallback(): Promise<SessionResult> {
    if (oauthCallbackPromise) return oauthCallbackPromise;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) return this.getSession();

    const storedState = this.getStoredOAuthState();
    if (!storedState || storedState.state !== state) {
      return {
        data: { session: null },
        error: new Error('Invalid OAuth state'),
      };
    }

    oauthCallbackPromise = this.exchangeOAuthCode({
      code,
      storedState,
      params,
    }).finally(() => {
      oauthCallbackPromise = null;
    });

    return oauthCallbackPromise;
  }

  private async exchangeOAuthCode({
    code,
    storedState,
    params,
  }: {
    code: string;
    storedState: StoredOAuthState;
    params: URLSearchParams;
  }): Promise<SessionResult> {
    try {
      const tokenResponse = await fetch(`${hostedUiBaseUrl}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: appClientId,
          code,
          redirect_uri: storedState.redirectTo,
          code_verifier: storedState.codeVerifier,
        }),
      });
      const tokenText = await tokenResponse.text();
      const tokenJson = tokenText ? JSON.parse(tokenText) : {};
      if (!tokenResponse.ok) {
        const message =
          tokenJson.error_description ||
          tokenJson.error ||
          `Cognito OAuth token exchange failed with ${tokenResponse.status}`;
        console.warn('Cognito OAuth token exchange failed', tokenJson);
        const existingSession = loadSession();
        if (existingSession?.accessToken) {
          this.clearOAuthCallbackParams(params);
          return { data: { session: existingSession }, error: null };
        }
        throw new Error(message);
      }
      const session = toAppSession({
        AccessToken: tokenJson.access_token,
        IdToken: tokenJson.id_token,
        RefreshToken: tokenJson.refresh_token,
        ExpiresIn: tokenJson.expires_in,
      });
      if (!session) {
        throw new Error('Cognito OAuth did not return a usable session');
      }
      saveSignedInSession(session);
      await persistRefreshToken(session);
      sessionStorage.removeItem(OAUTH_STORAGE_KEY);
      this.clearOAuthCallbackParams(params);
      announceSignIn(session);
      return { data: { session }, error: null };
    } catch (error) {
      return { data: { session: null }, error: error as Error };
    }
  }

  private clearOAuthCallbackParams(params: URLSearchParams) {
    params.delete('code');
    params.delete('state');
    const remainingSearch = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${remainingSearch ? `?${remainingSearch}` : ''}`
    );
  }

  async refreshSession(): Promise<SessionResult> {
    if (!refreshSessionPromise) {
      refreshSessionPromise = this.refreshSessionInternal({
        clearOnFailure: true,
      }).finally(() => {
        refreshSessionPromise = null;
      });
    }
    return refreshSessionPromise;
  }

  private async refreshSessionInternal(options: {
    clearOnFailure: boolean;
  }): Promise<SessionResult> {
    const existingSession = loadSession();
    if (!existingSession?.refreshToken) {
      try {
        const restored = await restoreSession();
        if (restored.accountSwitched) {
          return {
            data: { session: null },
            error: new Error(accountSwitchedFailure),
          };
        }
        if (!restored.stale && !restored.session) {
          saveSession(null);
          emitAuthChange('SIGNED_OUT', null);
        }
        return { data: { session: restored.session }, error: null };
      } catch (error) {
        if (options.clearOnFailure) {
          saveSession(null);
          emitAuthChange('SIGNED_OUT', null);
        }
        return { data: { session: null }, error: error as Error };
      }
    }

    try {
      const response = await cognitoRequest<{
        AuthenticationResult?: CognitoAuthResult;
      }>('InitiateAuth', {
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: appClientId,
        AuthParameters: {
          REFRESH_TOKEN: existingSession.refreshToken,
        },
      });
      const session = toAppSession(
        response.AuthenticationResult ?? {},
        existingSession.refreshToken
      );
      if (!session) {
        throw new Error('Cognito refresh did not return a usable session');
      }
      saveSession(session);
      // Only a rotated refresh token needs persisting. Rewriting an unchanged
      // one would overwrite the shared cookie if another tab has since signed
      // in a different account.
      if (response.AuthenticationResult?.RefreshToken) {
        await persistRefreshToken(session);
      }
      emitAuthChange('TOKEN_REFRESHED', session);
      return { data: { session }, error: null };
    } catch (error) {
      if (options.clearOnFailure || isInvalidRefreshTokenError(error)) {
        saveSession(null);
        await clearPersistedSession();
        emitAuthChange('SIGNED_OUT', null);
      }
      return { data: { session: null }, error: error as Error };
    }
  }

  async updateUser(attributes: {
    password?: string;
    data?: AuthMetadata;
  }): Promise<UserResult> {
    if (!attributes.password) return this.getUser();
    if (loadPendingNewPasswordChallenge()) {
      return this.completeNewPasswordChallenge(attributes.password);
    }

    const session = loadSession();
    if (!session?.accessToken) {
      return { data: { user: null }, error: new Error('No active session') };
    }

    try {
      const previousPassword = attributes.data?.currentPassword;
      if (typeof previousPassword !== 'string') {
        throw new Error('Current password is required to change password');
      }

      await cognitoRequest('ChangePassword', {
        AccessToken: session.accessToken,
        ProposedPassword: attributes.password,
        PreviousPassword: previousPassword,
      });
      return { data: { user: session.user }, error: null };
    } catch (error) {
      return { data: { user: session.user }, error: error as Error };
    }
  }

  async resetPasswordForEmail(email: string): Promise<{ error: Error | null }> {
    if (!isConfigured()) {
      return { error: new Error('Cognito is not configured') };
    }

    try {
      await cognitoRequest('ForgotPassword', {
        ClientId: appClientId,
        Username: email,
      });
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  async confirmForgotPassword(args: {
    email: string;
    code: string;
    password: string;
  }): Promise<{ error: Error | null }> {
    if (!isConfigured()) {
      return { error: new Error('Cognito is not configured') };
    }

    try {
      await cognitoRequest('ConfirmForgotPassword', {
        ClientId: appClientId,
        Username: args.email,
        ConfirmationCode: args.code,
        Password: args.password,
      });
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  async resend(args: {
    type: 'signup' | 'email_change';
    email: string;
  }): Promise<{ error: Error | null }> {
    if (!isConfigured()) {
      return { error: new Error('Cognito is not configured') };
    }

    if (args.type !== 'signup') {
      return { error: new Error('Resend is not supported for this action') };
    }
    try {
      await cognitoRequest('ResendConfirmationCode', {
        ClientId: appClientId,
        Username: args.email,
      });
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  async signOut(options?: {
    scope?: 'global' | 'local' | 'others';
  }): Promise<{ error: Error | null }> {
    if (options?.scope === 'others') {
      return {
        error: new Error(
          'Signing out other devices is not supported for Cognito sessions'
        ),
      };
    }

    const sessionBeforeRefresh = loadSession();
    const logoutSession =
      sessionBeforeRefresh?.expiresAt &&
      sessionBeforeRefresh.expiresAt - 60 <= Date.now() / 1000
        ? (await this.refreshSessionInternal({ clearOnFailure: false })).data
            .session
        : sessionBeforeRefresh;

    if (sessionBeforeRefresh && !logoutSession?.accessToken) {
      if (!loadSession()) {
        return { error: null };
      }
      return { error: new Error(secureLogoutFailure) };
    }

    if (logoutSession?.accessToken && apiBaseUrl) {
      try {
        const response = await fetch(`${apiBaseUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${logoutSession.accessToken}`,
          },
        });
        if (!response.ok) {
          if (isAlreadyLoggedOutStatus(response.status)) {
            await this.clearLocalSession();
            return { error: null };
          }
          return { error: new Error(secureLogoutFailure) };
        }
      } catch (err) {
        console.warn('auth: server-side logout call failed', err);
        return { error: new Error(secureLogoutFailure) };
      }
    }

    if (options?.scope === 'global' && logoutSession?.accessToken) {
      try {
        await cognitoRequest('GlobalSignOut', {
          AccessToken: logoutSession.accessToken,
        });
      } catch (error) {
        return { error: error as Error };
      }
    }

    await this.clearLocalSession();
    return { error: null };
  }

  async clearLocalSession(): Promise<void> {
    const signedOutSub = loadSession()?.user.id;
    savePendingNewPasswordChallenge(null);
    saveSession(null);
    await clearPersistedSession();
    emitAuthChange('SIGNED_OUT', null);
    if (signedOutSub) {
      postAuthBroadcast({ type: 'signed_out', sub: signedOutSub });
    }
  }

  onAuthStateChange(
    callback: (event: AuthChangeEvent, session: AppAuthSession | null) => void
  ): { data: { subscription: AuthSubscription } } {
    listeners.add(callback);
    queueMicrotask(() => callback('INITIAL_SESSION', loadSession()));
    return {
      data: {
        subscription: {
          unsubscribe: () => listeners.delete(callback),
        },
      },
    };
  }

  private getStoredOAuthState(): StoredOAuthState | null {
    const raw = sessionStorage.getItem(OAUTH_STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as StoredOAuthState;
    } catch {
      sessionStorage.removeItem(OAUTH_STORAGE_KEY);
      return null;
    }
  }

  private errorResult(message: string): SignInWithPasswordResult {
    return {
      data: { session: null, user: null },
      error: new Error(message),
    };
  }
}

function isInvalidRefreshTokenError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return /\b(invalid|expired|revoked)\b.*\brefresh\b|\brefresh\b.*\b(invalid|expired|revoked)\b/i.test(
    error.message
  );
}

function isAlreadyLoggedOutStatus(status: number): boolean {
  return status === 401 || status === 403;
}
